/**
 * One async user action (a form submit, a button): pending flag, translated error text, no double submit.
 * The API already answers in the UI language, so its message is shown as is; network failures get our own text.
 */
import { useCallback, useRef, useState } from "react";
import { useShellT } from "../i18n";
import { ApiError } from "./api";

export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const t = useShellT();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const busy = useRef(false);
  // Latest fn without changing run's identity (effects may depend on run).
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      if (busy.current) return undefined;
      busy.current = true;
      setPending(true);
      setError(null);
      try {
        return await fnRef.current(...args);
      } catch (err) {
        setError(err instanceof ApiError ? err : new ApiError(0, "INTERNAL", ""));
        return undefined;
      } finally {
        busy.current = false;
        setPending(false);
      }
    },
    [],
  );

  const message = error ? error.message || (error.code === "NETWORK" ? t("errors.network") : t("errors.generic")) : null;
  return { run, pending, error, message, reset: () => setError(null) };
}
